import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as moment_ from 'moment';
import { DatePickerComponent, Weekday } from '../date-picker/date-picker.component';
import { DatepickerRangeOption, DatepickerRangeOptions } from './date-picker-range.type';
import { getRangeValues, isEmpty, isUndefined } from '../../helpers/helpers';

const moment = moment_;
let $id = 0;

const labels = [
  'core.dateRange.allDates',
  'core.dateRange.today',
  'core.dateRange.yesterday',
  'core.dateRange.last.2days',
  'core.dateRange.last.3days',
  'core.dateRange.last.7days',
  'core.dateRange.last.30days',
  'core.dateRange.last.365days',
  'core.dateRange.last.24hours',
  'core.dateRange.last.48hours',
  'core.dateRange.last.72hours',
  'core.dateRange.tomorrow',
  'core.dateRange.next.2days',
  'core.dateRange.next.3days',
  'core.dateRange.next.7days',
  'core.dateRange.next.30days',
  'core.dateRange.customRange'
];

@Component({
  selector: 'wfm-date-picker-range',
  templateUrl: './date-picker-range.component.html',
  styleUrls: ['./date-picker-range.component.scss']
})
export class DatePickerRangeComponent implements OnInit, OnChanges {
  @Input() set id(val: string) {
    this.sid = val;
  }

  get id(): string {
    return this.sid || `${this.$id}`;
  }

  @Input() placeholder: string;
  @Input() required = false;

  @Input() startValue: moment_.Moment;
  @Output() startValueChange = new EventEmitter<moment_.Moment>();

  @Input() endValue: moment_.Moment;
  @Output() endValueChange = new EventEmitter<moment_.Moment>();

  @Input() format = 'MM-DD-YYYY';
  @Input() formatDelimiter = '-';

  @Input() disabled: boolean;

  @Input() min = moment('1799-01-01', 'YYYY-MM-DD');

  @Input() max: moment_.Moment;

  @Input() error: boolean;
  @Output() errorChange = new EventEmitter<boolean>();
  @Output() errorMessage = new EventEmitter<string>();

  @Input() firstDayOfWeek: Weekday = undefined;

  @Input() option: DatepickerRangeOptions;
  @Output() optionChange = new EventEmitter<DatepickerRangeOptions>();

  @Input() customRangeOnly = false;

  @Input() asButton = false;

  @Input() disableUserInput = false;

  @ViewChild(DatePickerComponent) child: DatePickerComponent;
  @ViewChild('dateRangePickerInput') dateRangePickerInput: ElementRef<HTMLElement>;

  @Output() outSideDatePickerRange = new EventEmitter<boolean>();

  input = '';
  hoverDataFormat = this.format;
  isOpen = false;
  invalidRange = false;
  startError = false;
  endError = false;

  options: Array<DatepickerRangeOption> = Object.values(DatepickerRangeOptions)
    .filter(v => !isNaN(Number(v)))
    .map((option, i) => ({
      label: labels[i],
      value: option as DatepickerRangeOptions,
      disabled: false
    }));
  selectedOption: DatepickerRangeOption = undefined;

  get isCustomRange(): boolean {
    return this.selectedOption?.value === DatepickerRangeOptions.customRange;
  }

  private sid = '';
  private $id = 0;

  private localDateFormats: { year: string[]; month: string[]; day: string[] } = {
    year: [],
    month: [],
    day: []
  };

  // used to restore the old input value when field gains focus
  // but loses it right after without changes
  private oldState: {
    input: string;
    startValue: moment_.Moment;
    endValue: moment_.Moment;
    selectedOption: { label: string; value: DatepickerRangeOptions };
  } = undefined;

  private window: Window = undefined;

  constructor(
    @Inject(DOCUMENT) private document: any,
    private translate: TranslateService,
    private cd: ChangeDetectorRef
  ) {
    this.window = (this.document as Document).defaultView;
    this.$id = ++$id;
  }

  ngOnInit(): void {
    this.formatChangeHandler();
    this.customRangeOnlyChangeHandler();
  }

  ngOnChanges(changes: SimpleChanges): void {
    changes?.format?.currentValue !== changes?.format?.previousValue && this.formatChangeHandler();
    if (
      changes?.customRangeOnly?.currentValue !== changes?.customRangeOnly?.previousValue ||
      (this.customRangeOnly &&
        (changes?.startValue?.currentValue !== changes?.startValue?.previousValue ||
          changes?.endValue?.currentValue !== changes?.endValue?.previousValue))
    ) {
      this.customRangeOnlyChangeHandler();
    }

    if (changes?.option?.currentValue !== changes?.option?.previousValue) {
      this.optionChangeHandler();
    }
  }

  private formatChangeHandler() {
    if (!this.format) {
      return this.errorHandler('format not provided');
    }
    const delimiter = this.format.replace(/\w/gi, '')[0];

    if (!delimiter) {
      return this.errorHandler('format delimiter not identified');
    }

    const placeholder = {
      YYYY: this.translate.instant('datepicker.placeholder.YYYY'),
      MM: this.translate.instant('datepicker.placeholder.MM'),
      MMM: this.translate.instant('datepicker.placeholder.MMM'),
      DD: this.translate.instant('datepicker.placeholder.DD')
    };

    this.hoverDataFormat = [
      placeholder.YYYY,
      this.format
        .replace('DD', '')
        .split(delimiter)
        .filter(v => v.trim().length)
        .map(s => placeholder[s])
        .join(delimiter),
      this.format
        .split(delimiter)
        .map(s => placeholder[s])
        .join(delimiter)
    ].join(', ');

    this.setLocalDateFormat();
  }

  private customRangeOnlyChangeHandler() {
    if (this.customRangeOnly) {
      this.window.setTimeout(() => {
        this.updateCustomRangeInput();
        this.option = DatepickerRangeOptions.customRange;
        this.selectedOption = this.options[this.options.length - 1];
        if (isEmpty(this.input)) {
          this.error = false;
        }
        this.optionChange.emit(this.option);
        this.cd.detectChanges();
      });
    } else if (!isUndefined(this.option)) {
      this.optionChangeHandler();
    }
  }

  private optionChangeHandler() {
    if (!isUndefined(this.option)) {
      this.selectedOption = this.options[this.option.valueOf()];
      this.selectedOptionChange(false);
      if (this.selectedOption?.value === DatepickerRangeOptions.customRange) {
        this.updateCustomRangeInput();
      }
    }
  }

  setValue() {
    if (!this.disableUserInput) {
      this.parseInput();

      if (this.input && this.startValue?.isValid() && this.endValue?.isValid()) {
        this.updateCustomRangeInput();
      }

      if (!this.input && this.oldState?.startValue === this.startValue && this.oldState?.endValue === this.endValue) {
        this.input = this.oldState.input;
        this.error = !!this.input && (!this.startValue?.isValid() || !this.endValue?.isValid());
      }

      this.oldState = undefined;
    } else if (!this.customRangeOnly) {
      if (isEmpty(this.input)) {
        this.error = true;
      }
      this.errorChange.emit(this.error);
    }
  }

  onFocus() {
    if (!this.error) {
      this.oldState = {
        input: this.input,
        startValue: this.startValue,
        endValue: this.endValue,
        selectedOption: this.selectedOption
      };

      this.input = '';
    }
  }

  parseInput() {
    const oldError = this.error;
    let error = false;

    if (this.input && !isEmpty(this.input) && this.input !== this.customRangeInput()) {
      const input = this.input.trim().replace(/[.\- _]/gi, '/');
      const date = this.parseDate(input);
      let valid = date.isValid();
      valid = valid && (!this.max || !date.isAfter(this.max));
      valid = valid && (!this.min || !date.isBefore(this.min));

      error = !valid;
      if (valid) {
        this.selectedOption = this.options[this.options.length - 1];
        this.selectedOptionChange(false);
        this.setCustomRangeFromPartial(date);
      } else {
        this.startValue = undefined;
        this.endValue = undefined;
      }
    }

    this.error = error;
    oldError !== error && this.errorChange.emit(this.error);
  }

  selectedOptionChange(clearInput = true) {
    const oldOption = this.option;

    if (clearInput) {
      this.input = '';
    }

    this.setRangeFromSelectedOption();

    if (!this.selectedOption || this.selectedOption.value !== DatepickerRangeOptions.customRange) {
      this.isOpen = false;
      this.input = (this.selectedOption && this.translate.instant(this.selectedOption.label)) || '';
      this.error = false;
    }

    this.option = this.selectedOption?.value;
    oldOption !== this.option && this.optionChange.emit(this.option);
    if (
      isEmpty(this.input) &&
      this.selectedOption?.value === DatepickerRangeOptions.customRange &&
      !this.startValue &&
      !this.endValue
    ) {
      this.error = true;
    }
    this.errorChange.emit(this.error);
  }

  reset() {
    const oldStartValue = this.startValue;
    const oldEndValue = this.endValue;

    this.startValue = undefined;
    this.endValue = undefined;

    oldStartValue !== this.startValue && this.startValueChange.emit(this.startValue);
    oldEndValue !== this.endValue && this.endValueChange.emit(this.endValue);
    this.child.clearInput();
    this.input = '';
    let error = false;
    if (this.option === DatepickerRangeOptions.customRange && !this.customRangeOnly) {
      error = true;
    }
    this.error = error;
    this.errorChange.emit(this.error);
  }

  apply() {
    const error = this.error;
    this.input = '';
    this.isOpen = false;

    this.startValueChange.emit(this.startValue);
    this.endValueChange.emit(this.endValue);

    if (!(this.startValue?.isValid() && this.endValue?.isValid()) || this.startValue?.isAfter(this.endValue)) {
      return this.errorHandler('invalid range');
    } else {
      this.errorChange.emit(false);
    }

    this.error = false;
    this.error !== error && this.errorChange.emit(this.error);
    this.updateCustomRangeInput();
  }

  onUserInputClick() {
    if (this.disableUserInput) {
      this.isOpen = true;
    }
  }

  clear() {
    this.error = false;
    this.input = '';
    this.startValue = undefined;
    this.endValue = undefined;
    this.child?.clearInput();
  }

  outSideDropDown() {
    this.outSideDatePickerRange.emit();
    if (this.error && this.isCustomRange && this.startValue.isValid() && this.endValue.isValid()) {
      let startValue, endValue;
      const oldError = this.error;
      if (moment(this.startValue.format('YYYY-MM-DD')).isAfter(this.endValue.format('YYYY-MM-DD'))) {
        startValue = moment(this.startValue.creationData().input, this.startValue.creationData().format);
        endValue = moment(this.endValue.creationData().input, this.endValue.creationData().format);
      } else {
        startValue = moment(this.startValue.format(this.format));
        endValue = moment(this.endValue.format(this.format));
      }
      if (startValue?.isValid()) {
        this.startValue = startValue;
        this.startError = false;
      }
      if (endValue?.isValid()) {
        this.endValue = endValue;
        this.endError = false;
      }
      this.error = this.startError || this.endError;
      this.error != oldError && this.errorChange.emit(this.error);
    }
  }

  private updateCustomRangeInput() {
    this.input = this.customRangeInput();
  }

  private customRangeInput(): string {
    if (this.startValue?.isValid() && this.endValue?.isValid()) {
      return [this.startValue?.format(this.format), this.endValue?.format(this.format)].join(' - ');
    }
    return '';
  }

  private setCustomRangeFromPartial(date: moment_.Moment) {
    let startDate = moment(date); // need to make a copy to save both start and end
    let endDate = date;
    const parseFormat: string = (date as any)._f;

    if (this.localDateFormats.year.indexOf(parseFormat) !== -1) {
      startDate = startDate.startOf('year');
      endDate = endDate.endOf('year');
    }

    if (this.localDateFormats.month.indexOf(parseFormat) !== -1) {
      startDate = startDate.startOf('month');
      endDate = endDate.endOf('month');
    }

    if (this.localDateFormats.day.indexOf(parseFormat) !== -1) {
      startDate = startDate.startOf('day');
      endDate = endDate.endOf('day');
    }

    if (startDate?.isValid()) {
      this.startValue = startDate;
      this.startValueChange.emit(startDate);
    }
    if (endDate?.isValid()) {
      this.endValue = endDate;
      this.endValueChange.emit(endDate);
      this.dateRangePickerInput?.nativeElement.blur();
    }
  }

  private parseDate(date) {
    const dateFormatArray = [].concat(
      this.localDateFormats.year,
      this.localDateFormats.month,
      this.localDateFormats.day
    );
    return moment(date, dateFormatArray, true);
  }

  private setLocalDateFormat() {
    this.localDateFormats = {
      year: ['YYYY'],
      month: [
        this.format.replace('DD', '').replace(/[\s.\\\/-]/gi, ''),
        this.cleanFormat(this.format.replace('DD', '').replace(/[\s.\\\/-]/gi, '/')),
        this.cleanFormat(
          this.format
            .replace('DD', '')
            .replace('MM', 'M')
            .replace(/[\s.\\\/-]/gi, '/')
        )
      ],
      day: [
        this.format.replace(/[\s.\\\/-]/gi, ''),
        this.cleanFormat(this.format.replace(/[\s.\\\/-]/gi, '/')),
        this.cleanFormat(this.format.replace('MM', 'M').replace(/[\s.\\\/-]/gi, '/')),
        this.cleanFormat(this.format.replace('DD', 'D').replace(/[\s.\\\/-]/gi, '/')),
        this.cleanFormat(
          this.format
            .replace('MM', 'M')
            .replace('DD', 'D')
            .replace(/[\s.\\\/-]/gi, '/')
        )
      ]
    };
  }

  private setRangeFromSelectedOption() {
    const oldStartValue = this.startValue;
    const oldEndValue = this.endValue;

    let option = this.selectedOption?.value;
    if (this.selectedOption?.value === DatepickerRangeOptions.customRange) {
      option = this.option; // old option
    }
    if (option) {
      const values = this.rangeCalculation[option]();
      if (values.startValue?.isValid()) {
        this.startValue = values.startValue;
      }
      if (values.endValue?.isValid()) {
        this.endValue = values.endValue;
      }
    } else {
      this.startValue = undefined;
      this.endValue = undefined;
    }

    oldStartValue !== this.startValue && this.startValueChange.emit(this.startValue);
    oldEndValue !== this.endValue && this.endValueChange.emit(this.endValue);
  }

  private errorHandler(message: string) {
    this.error = true;
    this.errorMessage.emit(`wfm-date-picker-range: ${message}`);
    this.errorChange.emit(this.error);
  }

  private cleanFormat(format) {
    // this code will fix formats from /m//yyyy to m/yyyy
    return format
      .split('/')
      .filter(v => v.trim().length)
      .join('/');
  }

  private rangeCalculation = [
    //all Dates
    () => ({ startValue: undefined, endValue: undefined }),
    // today
    () => ({
      startValue: moment().startOf('day'),
      endValue: moment().endOf('day')
    }),
    // yesterday
    this.rangeFactory(this.rangeInPastDays, 1),
    // last.2days
    this.rangeFactory(this.rangeInPastDays, 2),
    // last.3days
    this.rangeFactory(this.rangeInPastDays, 3),
    // last.7days
    this.rangeFactory(this.rangeInPastDays, 7),
    // last.30days
    this.rangeFactory(this.rangeInPastDays, 30),
    // last.365days
    this.rangeFactory(this.rangeInPastDays, 365),
    // last.24hours
    this.rangeFactory(this.rangeInPastHours, 24),
    // last.48hours
    this.rangeFactory(this.rangeInPastHours, 48),
    // last.72hours
    this.rangeFactory(this.rangeInPastHours, 72),
    // tomorrow
    this.rangeFactory(this.rangeInFutureDays, 1),
    // next.2days
    this.rangeFactory(this.rangeInFutureDays, 2),
    // next.3days
    this.rangeFactory(this.rangeInFutureDays, 3),
    // next.7days
    this.rangeFactory(this.rangeInFutureDays, 7),
    // next.30days
    this.rangeFactory(this.rangeInFutureDays, 30),
    // customRange
    () => getRangeValues(this.startValue, this.endValue)
  ];

  private rangeFactory(method, arg) {
    return () => method(arg);
  }

  private rangeInPastDays(days: number) {
    return {
      endValue: moment().subtract(1, 'days').endOf('day'),
      startValue: moment().subtract(days, 'days').startOf('day')
    };
  }

  private rangeInPastHours(hours: number) {
    return {
      endValue: moment(),
      startValue: moment().subtract(hours, 'hours')
    };
  }

  private rangeInFutureDays(days: number) {
    return {
      startValue: moment().add(1, 'day').startOf('day'),
      endValue: moment().add(days, 'days').endOf('day')
    };
  }
}
